import { useState, useCallback, useEffect, useRef } from "react";
import { isAxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";

const updateStatuses = ["idle", "saving", "saved", "error"];
export type UpdateStatus = (typeof updateStatuses)[number];

export function mergeStatus(
  status1: UpdateStatus,
  status2: UpdateStatus
): UpdateStatus {
  const priorities: Record<UpdateStatus, number> = {
    idle: 0,
    saved: 1,
    saving: 2,
    error: 3,
  };

  return priorities[status1] >= priorities[status2] ? status1 : status2;
}

type ValidationError = {
  message: string;
  errors: Array<{
    field: string;
    message: string;
  }>;
};

function isValidationError(error: unknown): error is ValidationError {
  return (
    typeof error === "object" &&
    error != null &&
    "message" in error &&
    "errors" in error
  );
}

export interface UseConfigManagerParams<
  DataType,
  UpdateRequestType,
  UpdateResponseType
> {
  name: string; // used for query key
  getConfig: () => Promise<DataType>;
  updateConfig: (
    request: UpdateRequestType,
    signal?: AbortSignal
  ) => Promise<UpdateResponseType>;
  debounceTime?: number;
}

export function useConfigManager<
  DataType,
  UpdateRequestType,
  UpdateResponseType = DataType
>({
  name,
  getConfig,
  updateConfig,
  debounceTime = 1000,
}: UseConfigManagerParams<DataType, UpdateRequestType, UpdateResponseType>) {
  const { data, isLoading, refetch } = useQuery<DataType>({
    queryKey: ["config-Manager", "config", name],
    queryFn: getConfig,
    enabled: true,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const { mutateAsync } = useMutation<
    UpdateResponseType,
    unknown,
    UpdateRequestType
  >({
    mutationKey: ["config-Manager", "update", name],
    mutationFn: async (request) => {
      abortControllerRef.current?.abort();

      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;

      return updateConfig(request, newAbortController.signal);
    },
    onMutate: () => {
      setErrors(undefined);
      setUpdateStatus("saving");
    },
    onSuccess: async () => {
      await refetch();
      setUpdateStatus("saved");
    },
    onError: (error) => {
      setUpdateStatus("error");
      if (isAxiosError(error) && isValidationError(error.response?.data)) {
        setErrors(
          error.response?.data.errors.reduce(
            (acc, { field, message }) => ({
              ...acc,
              [field]: message,
            }),
            {}
          )
        );
      }
    },
  });

  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>("idle");
  const [localConfiguration, setLocalConfiguration] = useState<DataType | null>(
    null
  );
  const [errors, setErrors] = useState<Record<string, string> | undefined>();

  const debouncedUpdate = useDebouncedCallback<
    Promise<UpdateResponseType | undefined>,
    [UpdateRequestType]
  >(mutateAsync, debounceTime);

  const handleUpdate = useCallback(
    (request: UpdateRequestType) => {
      setLocalConfiguration((prevState) =>
        prevState ? { ...prevState, ...request } : null
      );
      debouncedUpdate(request);
    },
    [debouncedUpdate]
  );

  useEffect(() => {
    if (data != null) {
      setLocalConfiguration(data);
    }
  }, [data]);

  useEffect(() => {
    if (updateStatus === "saved") {
      const timer = setTimeout(() => setUpdateStatus("idle"), 2000);
      return () => clearTimeout(timer);
    }
  }, [updateStatus]);

  return {
    data: localConfiguration,
    isLoading,
    update: handleUpdate,
    updateStatus,
    errors,
  };
}
